import React, { useState, useEffect } from "react";
import { Plus, Trash2, Info, Loader2 } from "lucide-react";
import { Button } from "../GraderUI";
import {
	createAiVisibilityCustomPrompt,
	deleteAiVisibilityCustomPrompt,
} from "../../../services/aiVisibilityApi";

function formatPromptDate(value) {
	if (!value) return "—";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return "—";
	return date.toLocaleDateString();
}

function formatRate(value) {
	const n = Number(value);
	if (!Number.isFinite(n)) return "0%";
	return `${n % 1 === 0 ? n : n.toFixed(1)}%`;
}

function DeleteConfirmModal({ target, onClose, onConfirm, deleting, error }) {
	if (!target) return null;

	return (
		<div className="grader-dialog" onClick={onClose} role="presentation">
			<div
				className="grader-dialog__content custom-citation-questions__modal"
				onClick={(e) => e.stopPropagation()}
				role="dialog"
				aria-modal="true"
			>
				<div className="grader-dialog__header">
					<h3 className="grader-dialog__title">Delete custom prompt?</h3>
					<button
						type="button"
						className="grader-dialog__close"
						onClick={onClose}
						disabled={deleting}
						aria-label="Close"
					>
						×
					</button>
				</div>

				<p className="custom-citation-questions__delete-modal-text">
					This cannot be undone. The prompt will be removed from the citation grid.
				</p>
				<p className="custom-citation-questions__delete-modal-prompt" title={target.question}>
					{target.question}
				</p>

				{error && (
					<p className="custom-citation-questions__error" role="alert">
						{error}
					</p>
				)}

				<div className="custom-citation-questions__modal-actions">
					<Button type="button" variant="outline" onClick={onClose} disabled={deleting}>
						Cancel
					</Button>
					<Button
						type="button"
						className="custom-citation-questions__delete-btn"
						onClick={onConfirm}
						disabled={deleting}
					>
						{deleting ? "Deleting…" : "Delete"}
					</Button>
				</div>
			</div>
		</div>
	);
}

function AddCustomPromptModal({ open, onClose, onSubmit, submitting, error }) {
	const [question, setQuestion] = useState("");

	useEffect(() => {
		if (!open) {
			setQuestion("");
		}
	}, [open]);

	if (!open) return null;

	const handleSubmit = (e) => {
		e.preventDefault();
		onSubmit(question);
	};

	return (
		<div className="grader-dialog" onClick={onClose} role="presentation">
			<div
				className="grader-dialog__content custom-citation-questions__modal"
				onClick={(e) => e.stopPropagation()}
				role="dialog"
				aria-modal="true"
			>
				<div className="grader-dialog__header">
					<h3 className="grader-dialog__title">Add Custom Prompt</h3>
					<button
						type="button"
						className="grader-dialog__close"
						onClick={onClose}
						disabled={submitting}
						aria-label="Close"
					>
						×
					</button>
				</div>

				<form onSubmit={handleSubmit}>
					<p className="custom-citation-questions__modal-desc">
						Enter a prompt to test how AI models respond and whether they cite your website.
						It will be tested with ChatGPT, Gemini, and Claude across Organic and Web Search.
					</p>

					<textarea
						className="grader-input custom-citation-questions__modal-textarea"
						placeholder="e.g. What is the best pricing plan for your product?"
						value={question}
						onChange={(e) => setQuestion(e.target.value)}
						rows={4}
						disabled={submitting}
						required
					/>

					{submitting && (
						<div className="custom-citation-questions__modal-loading">
							<Loader2 className="custom-citation-questions__modal-spinner" />
							<div>
								<p className="custom-citation-questions__modal-loading-title">
									Testing prompt with AI models…
								</p>
								<p className="custom-citation-questions__modal-loading-sub">
									This may take 10–15 seconds
								</p>
							</div>
						</div>
					)}

					{error && (
						<p className="custom-citation-questions__error" role="alert">
							{error}
						</p>
					)}

					<div className="custom-citation-questions__modal-actions">
						<Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
							Cancel
						</Button>
						<Button type="submit" disabled={submitting || !question.trim()}>
							{submitting ? "Testing…" : "Add Prompt"}
						</Button>
					</div>
				</form>
			</div>
		</div>
	);
}

export function CustomCitationQuestions({
	websiteId,
	auditId,
	prompts = [],
	summary = null,
	loading = false,
	listError = null,
	onPromptsChanged,
	onReportRefresh,
}) {
	const [modalOpen, setModalOpen] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [submitError, setSubmitError] = useState(null);
	const [deleteTarget, setDeleteTarget] = useState(null);
	const [deletingId, setDeletingId] = useState(null);
	const [deleteError, setDeleteError] = useState(null);

	const handleRefreshAll = async () => {
		if (onPromptsChanged) {
			await onPromptsChanged();
		}
		if (onReportRefresh) {
			await onReportRefresh({ silent: true });
		}
	};

	const handleCreate = async (question) => {
		setSubmitting(true);
		setSubmitError(null);

		const result = await createAiVisibilityCustomPrompt(websiteId, auditId, question);
		setSubmitting(false);

		if (!result.ok) {
			setSubmitError(result.message || "Failed to add custom prompt");
			return;
		}

		setModalOpen(false);
		await handleRefreshAll();
	};

	const handleDeleteConfirm = async () => {
		if (!deleteTarget) return;

		setDeletingId(deleteTarget.id);
		setDeleteError(null);

		const result = await deleteAiVisibilityCustomPrompt(websiteId, auditId, deleteTarget.id);
		setDeletingId(null);

		if (!result.ok) {
			setDeleteError(result.message || "Failed to delete custom prompt");
			return;
		}

		setDeleteTarget(null);
		await handleRefreshAll();
	};

	const customCount =
		summary?.customPromptCount != null && summary.customPromptCount > 0
			? summary.customPromptCount
			: prompts.length;
	const hasCustomPrompts = !loading && prompts.length > 0;
	const summaryLabel =
		customCount === 1
			? "Updated Citation Rate (with 1 custom prompt)"
			: `Updated Citation Rate (with ${customCount} custom prompts)`;

	return (
		<div className="custom-citation-questions">
			<div className="custom-citation-questions__header">
				<div>
					<h3 className="custom-citation-questions__title">Custom Prompts</h3>
					<p className="custom-citation-questions__subtitle">
						Add your own prompts — they run through the same AI models and appear in the
						Citation Results grid above (Organic + Web Search).
					</p>
				</div>
				<Button onClick={() => setModalOpen(true)}>
					<Plus className="citation-tracking__methodology-icon" style={{ marginRight: "0.5rem" }} />
					Add Prompt
				</Button>
			</div>

			{hasCustomPrompts && summary && (
				<div className="custom-citation-questions__summary">
					<div className="custom-citation-questions__summary-left">
						<p className="custom-citation-questions__summary-label">{summaryLabel}</p>
						<p className="custom-citation-questions__summary-meta">
							Total: {summary.totalPrompts} prompts • {summary.totalCitations} citations
						</p>
					</div>
					<div className="custom-citation-questions__summary-right">
						<p className="custom-citation-questions__summary-rate">
							{formatRate(summary.updatedCitationRate)}
						</p>
						<p className="custom-citation-questions__summary-providers">
							ChatGPT: {formatRate(summary.perProvider.openai)} Gemini:{" "}
							{formatRate(summary.perProvider.gemini)} Claude:{" "}
							{formatRate(summary.perProvider.claude)}
						</p>
					</div>
				</div>
			)}

			{hasCustomPrompts && (
				<p className="custom-citation-questions__info">
					<Info className="custom-citation-questions__info-icon" />
					Detailed results for these prompts are shown in the grid above — switch Organic / Web
					Search tabs and Default / Compare mode there.
				</p>
			)}

			{listError && (
				<p className="custom-citation-questions__error" role="alert">
					{listError}
				</p>
			)}

			{loading && (
				<div className="custom-citation-questions__loading">Loading custom prompts…</div>
			)}

			{!loading && hasCustomPrompts && (
				<ul className="custom-citation-questions__list">
					{prompts.map((prompt) => (
						<li key={prompt.id} className="custom-citation-questions__item">
							<span className="custom-citation-questions__badge">Custom</span>
							<p className="custom-citation-questions__question" title={prompt.question}>
								{prompt.question}
							</p>
							<span className="custom-citation-questions__meta">
								{prompt.citedCount}/{prompt.providerCount} cited
							</span>
							<span className="custom-citation-questions__date">
								{formatPromptDate(prompt.createdAt)}
							</span>
							<button
								type="button"
								className="custom-citation-questions__delete"
								onClick={() => {
									setDeleteError(null);
									setDeleteTarget(prompt);
								}}
								disabled={deletingId === prompt.id}
								aria-label="Delete custom prompt"
							>
								<Trash2 size={16} />
							</button>
						</li>
					))}
				</ul>
			)}

			{!loading && !hasCustomPrompts && (
				<div className="custom-citation-questions__empty">
					<p className="custom-citation-questions__empty-text">No custom prompts yet</p>
					<Button variant="outline" onClick={() => setModalOpen(true)}>
						<Plus
							className="citation-tracking__methodology-icon"
							style={{ marginRight: "0.5rem" }}
						/>
						Add Your First Prompt
					</Button>
				</div>
			)}

			<AddCustomPromptModal
				open={modalOpen}
				onClose={() => {
					if (!submitting) {
						setModalOpen(false);
						setSubmitError(null);
					}
				}}
				onSubmit={handleCreate}
				submitting={submitting}
				error={submitError}
			/>

			<DeleteConfirmModal
				target={deleteTarget}
				onClose={() => {
					if (!deletingId) {
						setDeleteTarget(null);
						setDeleteError(null);
					}
				}}
				onConfirm={handleDeleteConfirm}
				deleting={Boolean(deletingId)}
				error={deleteError}
			/>
		</div>
	);
}

export default CustomCitationQuestions;
